import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import { Ticket, Buildings, FolderSimple, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { ROLE_LABELS } from "../lib/utils";

function Shell({ children, brand }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md surface-card shadow-sm p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center overflow-hidden">
            {brand?.logo_url
              ? <img src={brand.logo_url} alt="" className="w-full h-full object-contain" />
              : <Ticket weight="fill" size={20} className="text-white" />}
          </div>
          <div className="font-display font-bold text-lg tracking-tight">{brand?.name || "Support By ERP4U"}</div>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, applySession } = useAuth();

  const [info, setInfo] = useState(undefined); // undefined=loading, null=not found
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/invites/${token}/info`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null));
  }, [token]);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`, { name, password });
      applySession(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (info === undefined) return <Shell><p className="text-sm text-slate-500">Loading invitation…</p></Shell>;

  if (info === null || !info.valid) {
    return (
      <Shell>
        <h2 className="font-display text-xl font-bold text-slate-900">Invitation unavailable</h2>
        <p className="text-sm text-slate-500 mt-2">
          {info && info.status === "accepted"
            ? "This invitation has already been used. Try signing in instead."
            : "This invitation link is invalid, revoked, or has expired. Please ask for a new one."}
        </p>
        <Link to="/login" className="inline-block mt-5 text-sm text-blue-600 hover:underline font-medium">Go to sign in</Link>
      </Shell>
    );
  }

  return (
    <Shell brand={info.company}>
      <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Accept your invitation</h2>
      <p className="text-sm text-slate-500 mt-1.5">
        {info.invited_by_name} invited <span className="font-medium text-slate-700">{info.email}</span> to join {info.company?.name || "Support By ERP4U"}.
      </p>

      <div className="mt-4 space-y-1.5 bg-slate-50 border border-border rounded-md p-3 text-sm">
        {info.organization_name && (
          <div className="flex items-center gap-2 text-slate-700"><Buildings size={14} className="text-blue-600" /> {info.organization_name}</div>
        )}
        {info.organization_name && (
          <div className="flex items-center gap-2 text-slate-700">
            <FolderSimple size={14} className="text-blue-600" /> {info.all_projects ? "All projects" : (info.project_name || "—")}
          </div>
        )}
        <div className="flex items-center gap-2 text-slate-700"><CheckCircle size={14} className="text-blue-600" /> Role: {ROLE_LABELS[info.role]}</div>
      </div>

      <form onSubmit={submit} className="space-y-4 mt-5" data-testid="invite-form">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</Label>
          <Input value={info.email} disabled />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Full name</Label>
          <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Create a password</Label>
          <PasswordInput required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password-input" />
        </div>
        {error && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}
        <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="invite-submit-button">
          {loading ? "Creating account…" : "Create account & join"}
        </Button>
      </form>

      <div className="text-xs text-slate-500 text-center mt-5">
        Already have an account?{" "}
        <Link to="/login" className="text-blue-600 hover:underline font-medium">Sign in</Link>
      </div>
    </Shell>
  );
}
